import AssistantIcon from "@mui/icons-material/Assistant";
import ButtonBase from "@mui/material/ButtonBase";
import { styled } from "@mui/material/styles";
import {
  ASSISTANT_LAUNCHER_ATTRIBUTE,
  ASSISTANT_LAUNCHER_LABEL,
  ASSISTANT_PANEL_ID,
  useAssistantDock,
} from "ai-assistant/dock/assistant-dock";
import { SESSION_RECORDING_UNMASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import React from "react";

/** The bottom bar's height, which the shell reserves so the bar covers nothing. */
export const ASSISTANT_BAR_HEIGHT_PX = 56;

const LauncherButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  backgroundImage: `linear-gradient(90deg, ${theme.palette.common.white} 0%, #e3f2fd 100%)`,
  border: "2px solid #90caf9",
  boxSizing: "border-box",
  color: theme.palette.primary.dark,
  display: "flex",
  font: "inherit",
  fontSize: theme.typography.body1.fontSize,
  fontWeight: 500,
  gap: theme.spacing(1.25),
  justifyContent: "flex-start",
  padding: theme.spacing(1.25, 1.5),
  width: "100%",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
  "& .assistant-launcher-icon": {
    color: "rgb(0, 149, 255)",
  },
}));
const NavEntryButton = styled(LauncherButton)(({ theme }) => ({
  borderRadius: theme.spacing(1.25),
}));
const BarButton = styled(LauncherButton)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  maxWidth: 560,
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
}));
const NavEntryContainer = styled("div")(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1.5, 1.5, 0.5),
}));
/**
 * One line always. The entry's icon, label and shortcut need 235px in Roboto and the 17rem nav gives
 * them 247px, which held only while the nav kept its scrollbar off the entry; should that ever come
 * back, the label ends in an ellipsis rather than wrapping.
 */
const Label = styled("span")({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textAlign: "left",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
const Shortcut = styled("kbd")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontFamily: "inherit",
  fontSize: theme.typography.caption.fontSize,
  padding: theme.spacing(0, 0.75),
  whiteSpace: "nowrap",
}));
const Bar = styled("div")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxSizing: "border-box",
  display: "flex",
  flexShrink: 0,
  height: ASSISTANT_BAR_HEIGHT_PX,
  justifyContent: "center",
  padding: theme.spacing(0.75, 1.5),
}));

/** What both launchers carry: the shared name, the panel they open, and how they open it. */
function useLauncherProps() {
  const dock = useAssistantDock();
  return {
    [ASSISTANT_LAUNCHER_ATTRIBUTE]: "",
    "aria-controls": dock.isOpen ? ASSISTANT_PANEL_ID : undefined,
    "aria-expanded": dock.isOpen,
    "aria-keyshortcuts": dock.keyShortcuts,
    onClick: (event: React.MouseEvent<HTMLElement>) => dock.toggle(event.currentTarget),
    shortcutLabel: dock.shortcutLabel,
  };
}

/**
 * The assistant's entry at the top of the side nav, where the nav is a column (`lg` and up).
 *
 * Opening it puts the assistant where the nav was (#6128), so the entry is only ever seen while the
 * assistant is closed.
 */
export function AssistantNavEntry() {
  const { shortcutLabel, ...launcher } = useLauncherProps();

  return (
    <NavEntryContainer>
      <NavEntryButton {...launcher}>
        <AssistantIcon className="assistant-launcher-icon" fontSize="small" />
        <Label>{ASSISTANT_LAUNCHER_LABEL}</Label>
        <Shortcut aria-hidden="true">{shortcutLabel}</Shortcut>
      </NavEntryButton>
    </NavEntryContainer>
  );
}

/**
 * The bar that opens the assistant where there is no nav column: below `lg`, and wherever the nav
 * is hidden. At the bottom, in thumb reach on a phone; the shell gives it its own row, so it is
 * never drawn over a page.
 */
export function AssistantBar() {
  const { shortcutLabel, ...launcher } = useLauncherProps();

  return (
    // Its label is fixed, so session replays may read it (#5168).
    <Bar className={SESSION_RECORDING_UNMASK_CLASS}>
      <BarButton {...launcher}>
        <AssistantIcon className="assistant-launcher-icon" fontSize="small" />
        <Label>{ASSISTANT_LAUNCHER_LABEL}</Label>
        <Shortcut aria-hidden="true" sx={{ display: { xs: "none", md: "inline" } }}>{shortcutLabel}</Shortcut>
      </BarButton>
    </Bar>
  );
}
